import React from 'react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  ComposedChart, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import './TokenUsageChart.css';

const TokenUsageChart = ({ usageData, chartType = 'line' }) => {
  if (!usageData?.length) {
    return (
      <section className="chart-container" aria-label="Token Usage Chart">
        <p className="no-data">No data available</p>
      </section>
    );
  }

  const sharedProps = {
    data: usageData,
    margin: { top: 20, right: 30, left: 20, bottom: 5 }
  };

  const colors = {
    tokenize: 'var(--color-tokenize, #2363eb)',
    detokenize: 'var(--color-detokenize, #82ca9d)',
  };

  switch (chartType) {
    case 'bar':
      return (
        <section className="chart-container" aria-label="Bar chart showing token usage">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart {...sharedProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" />
              <XAxis dataKey="date" stroke="var(--text-secondary)" />
              <YAxis stroke="var(--text-secondary)" />
              <Tooltip contentStyle={{ backgroundColor: 'var(--bg-surface)', borderRadius: 8 }} />
              <Legend />
              <Bar dataKey="tokenize" fill={colors.tokenize} />
              <Bar dataKey="detokenize" fill={colors.detokenize} />
            </BarChart>
          </ResponsiveContainer>
        </section>
      );
    case 'line':
      return (
        <section className="chart-container" aria-label="Line chart showing token usage">
          <ResponsiveContainer width="100%" height={350}>
            <LineChart {...sharedProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" />
              <XAxis dataKey="date" stroke="var(--text-secondary)" />
              <YAxis stroke="var(--text-secondary)" />
              <Tooltip contentStyle={{ backgroundColor: 'var(--bg-surface)', borderRadius: 8 }} />
              <Legend />
              <Line type="monotone" dataKey="tokenize" stroke={colors.tokenize} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="detokenize" stroke={colors.detokenize} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </section>
      );
    case 'area':
      return (
        <section className="chart-container" aria-label="Area chart showing token usage">
          <ResponsiveContainer width="100%" height={350}>
            <AreaChart {...sharedProps}>
              <defs>
                <linearGradient id="colorTokenize" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={colors.tokenize} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={colors.tokenize} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorDetokenize" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={colors.detokenize} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={colors.detokenize} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" stroke="var(--text-secondary)" />
              <YAxis stroke="var(--text-secondary)" />
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" />
              <Tooltip contentStyle={{ backgroundColor: 'var(--bg-surface)', borderRadius: 8 }} />
              <Legend />
              <Area type="monotone" dataKey="tokenize" stroke={colors.tokenize} fill="url(#colorTokenize)" />
              <Area type="monotone" dataKey="detokenize" stroke={colors.detokenize} fill="url(#colorDetokenize)" />
            </AreaChart>
          </ResponsiveContainer>
        </section>
      );
    case 'composed':
      return (
        <section className="chart-container" aria-label="Composed chart showing token usage">
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart {...sharedProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" />
              <XAxis dataKey="date" stroke="var(--text-secondary)" />
              <YAxis stroke="var(--text-secondary)" />
              <Tooltip contentStyle={{ backgroundColor: 'var(--bg-surface)', borderRadius: 8 }} />
              <Legend />
              <Bar dataKey="tokenize" barSize={20} fill={colors.tokenize} />
              <Line type="monotone" dataKey="detokenize" stroke={colors.detokenize} strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </section>
      );
    case 'radar': {
      const radarData = usageData.map(d => ({
        date: d.date,
        Tokenize: d.tokenize,
        Detokenize: d.detokenize,
      }));
      return (
        <section className="chart-container" aria-label="Radar chart showing token usage">
          <ResponsiveContainer width="100%" height={350}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="var(--border-default)" />
              <PolarAngleAxis dataKey="date" stroke="var(--text-secondary)" />
              <PolarRadiusAxis stroke="var(--text-secondary)" />
              <Radar name="Tokenize" dataKey="Tokenize" stroke={colors.tokenize} fill={colors.tokenize} fillOpacity={0.6} />
              <Radar name="Detokenize" dataKey="Detokenize" stroke={colors.detokenize} fill={colors.detokenize} fillOpacity={0.6} />
              <Legend />
              <Tooltip contentStyle={{ backgroundColor: 'var(--bg-surface)', borderRadius: 8 }} />
            </RadarChart>
          </ResponsiveContainer>
        </section>
      );
    }
    default:
      return (
        <section className="chart-container" aria-label="Unsupported chart type message">
          <p className="no-data">Unsupported chart type.</p>
        </section>
      );
  }
};

export default TokenUsageChart;
